<script setup lang="ts">
import { ElMessage, ElMessageBox } from "element-plus";
import { ref } from "vue";
import {
  FormElementRule,
  FormElementType,
  FormSectionRule,
  FormTextFormElement,
  type FormDefinition,
  type FormElementBase,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { isFormElementBasedCondition } from "~/utils/typeGuards";

/** current open/close state of the dialog */
const dialogVisible = defineModel<boolean>({ required: true });

const props = defineProps<{
  formDefinitionGuid: string;
}>();

const emit = defineEmits<{
  (e: "update:form", value: FormDefinition): void;
}>();

const localForm = ref<FormDefinition>();
const formJSON = ref("");

const adjustSavedValues = (form: FormDefinition) => {
  form.components.forEach((c) => {
    switch (c.type) {
      case FormElementType.FORM_TEXT: {
        // the tinyMCE editor sometimes adds a trailing blank line, especially after an <hr>, which we don't want to save
        const trailingBlankLine = "\n<p>&nbsp;</p>";
        const el: FormTextFormElement = c as FormTextFormElement;
        let text = el.properties.content ?? "";
        let changeCount = 0;
        while (text.endsWith(trailingBlankLine)) {
          text = text.slice(0, 0 - trailingBlankLine.length);
          changeCount++;
        }
        if (changeCount) {
          // replace it
          el.properties.content = text;
        }
        break;
      }
    }
  });
  return form;
};

const handleSaveChanges = async () => {
  if (!localForm.value) {
    showError("Form not found");
    return;
  }
  await apiClient.formDefinition
    .updateFormDefinition(
      props.formDefinitionGuid,
      adjustSavedValues(localForm.value),
    )
    .then((res) => {
      localForm.value = res;
      emit("update:form", res);
    })
    .catch(showError);
};

const handleUploadFormJSON = async () => {
  if (!formJSON.value) {
    showError("No JSON provided");
    return;
  }
  try {
    const newForm: FormDefinition = JSON.parse(formJSON.value);

    const guidQuestionCodeMap = new Map<string, string>();
    newForm.components.forEach((c: FormElementBase) => {
      guidQuestionCodeMap.set(c.guid, c.questionCode);
    });

    newForm.guid = props.formDefinitionGuid;

    await apiClient.formDefinition
      .uploadFormJson(props.formDefinitionGuid, newForm)
      .then((res) => {
        localForm.value = res;
      });

    if (!localForm.value) {
      showError("Form not found");
      return;
    }

    // update all the rules to point to the new guids
    const rules = newForm.rules.map((r: FormElementRule) => {
      const elementGuid = localForm.value?.components.find(
        (c) => guidQuestionCodeMap.get(r.elementGuid) === c.questionCode,
      )?.guid;
      if (!elementGuid) {
        showError("Element guid not found");
        return r;
      }
      r.elementGuid = elementGuid;
      // @ts-expect-error - we want the server to generate a new guid
      r.guid = undefined;
      const newConditions = r.conditions.map((c) => {
        const newCondition = { ...c };

        // only form element based conditions have a question code / question guid
        if (
          isFormElementBasedCondition(newCondition) &&
          isFormElementBasedCondition(c)
        ) {
          // update the question guid
          const qGuid = localForm.value?.components.find(
            (comp) =>
              guidQuestionCodeMap.get(c.questionGuid ?? "") ===
              comp.questionCode,
          )?.guid;

          if (!qGuid) {
            showError("Question guid not found");
            return c;
          }

          newCondition.questionGuid = qGuid;
        }
        return newCondition;
      });

      const newRule = { ...r, conditions: newConditions };

      return newRule;
    });

    localForm.value.rules = [...rules];

    // update section rules to point to the new guids
    const sectionRules = newForm.sectionRules.map((r: FormSectionRule) => {
      const elementGuid = localForm.value?.components.find(
        (c) => guidQuestionCodeMap.get(r.sectionGuid) === c.questionCode,
      )?.guid;
      if (!elementGuid) {
        showError("Element guid not found");
        return r;
      }

      r.sectionGuid = elementGuid;
      r.formDefinitionGuid = props.formDefinitionGuid;
      // @ts-expect-error - we want the server to generate a new guid
      r.guid = undefined;

      return r;
    });

    localForm.value.sectionRules = [...sectionRules];

    // save the changes
    await handleSaveChanges();

    ElMessage.success("Form uploaded successfully");

    dialogVisible.value = false;
  } catch (e) {
    showError(e);
  }
};

const handleClose = () => {
  if (!formJSON.value) {
    dialogVisible.value = false;
    return;
  }
  ElMessageBox.confirm("Are you sure to close this dialog?")
    .then(() => {
      dialogVisible.value = false;
    })
    .catch(showError);
};
</script>

<template id="form-simple-text-option">
  <el-dialog
    v-model="dialogVisible"
    title="Upload Form JSON"
    width="500"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleClose"
  >
    <div>
      Please paste the JSON of the form you would like to upload. The JSON
      should be in the same format as the form JSON that is returned from the
      API. This will overwrite the current form. Doesn't currently support form
      elements with events.
    </div>
    <el-input
      v-model="formJSON"
      type="textarea"
      :rows="10"
      :autosize="{ minRows: 10 }"
    ></el-input>

    <template #footer>
      <div class="dialog-footer">
        <el-button type="danger" plain @click="handleClose">Cancel</el-button>
        <el-button type="primary" @click="handleUploadFormJSON"
          >Upload</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<style lang="less">
.form-item-wide-no-label {
  flex-direction: column;
  .el-form-item__label-wrap {
    display: none;
  }
}
</style>
